import { assert$DisplayableError } from "kreate-common/modules/displayable-error";
import ButtonOutline from "kreate-common/modules/kreate-ui/components/ButtonOutline";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import React from "react";

import FieldNumEditions from "../ModalMintAndList/fields/FieldNumEditions";
import { useFieldNumEditions } from "../ModalMintAndList/fields/FieldNumEditions/hooks";

import RadioGroup from "./components/RadioGroup";
import SummaryDescription from "./components/SummaryDescription";
import styles from "./index.module.scss";

import { useBusyCount } from "@/modules/common-hooks/useBusyCount";

type SubmitEvent =
  | { type: "skip-wallet-editions" }
  | { type: "transfer-to-wallet"; numEditions: number }
  | { type: "transfer-from-wallet"; numEditions: number };

export type ModalUpdateListingDetailsConfirmation$SubmitEvent = SubmitEvent;

type Props = {
  open: boolean;
  onCancel?: () => void;
  onSubmit?: (event: SubmitEvent) => void;
  numEditionsOld: number;
  numEditionsNew: number;
  numEditionsInWallet: number;
  listingPrice: number;
};

export default function ModalUpdateListingDetailsConfirmation({
  open,
  onCancel,
  onSubmit,
  numEditionsOld,
  numEditionsNew,
  numEditionsInWallet,
  listingPrice,
}: Props) {
  const [selectedOption, setSelectedOption] = React.useState<
    "none" | "max" | "custom"
  >("none");

  const maxWalletDelta = Math.min(
    Math.abs(numEditionsNew - numEditionsOld),
    numEditionsOld < numEditionsNew ? numEditionsInWallet : Infinity
  );

  const constantSum = Math.abs(numEditionsNew - numEditionsOld);

  const fieldNumEditionsFromOrToWallet = useFieldNumEditions({
    initialValue: 0,
    min: 0,
    max: maxWalletDelta,
  });

  const fieldNumEditionsMintedOrBurned = useFieldNumEditions({
    initialValue: Math.abs(numEditionsNew - numEditionsOld),
    min: constantSum - maxWalletDelta,
    max: constantSum,
  });

  // NOTE: @kien-kreate: every time a field change, we update the other field
  // to preserve the sum (a + b = const). Although using `React.useEffect`
  // is not the proper way, it is the cheapest way.

  React.useEffect(() => {
    if (fieldNumEditionsFromOrToWallet.value == null) return;
    const newValue = constantSum - fieldNumEditionsFromOrToWallet.value;
    if (fieldNumEditionsMintedOrBurned.value !== newValue) {
      fieldNumEditionsMintedOrBurned.setValue(newValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldNumEditionsFromOrToWallet.value]);

  React.useEffect(() => {
    if (fieldNumEditionsMintedOrBurned.value == null) return;
    const newValue = constantSum - fieldNumEditionsMintedOrBurned.value;
    if (fieldNumEditionsFromOrToWallet.value !== newValue) {
      fieldNumEditionsFromOrToWallet.setValue(newValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldNumEditionsMintedOrBurned.value]);

  const { busy, withDefaultErrorHandler } = useBusyCount();

  const handleSubmit = withDefaultErrorHandler(() => {
    const event: SubmitEvent = (() => {
      const delta = (() => {
        switch (selectedOption) {
          case "none":
            return 0;
          case "max":
            return maxWalletDelta;
          case "custom": {
            const delta =
              fieldNumEditionsFromOrToWallet.validateBeforeSubmitting();
            const compl =
              fieldNumEditionsMintedOrBurned.validateBeforeSubmitting();

            // NOTE: @kien-kreate: The following error should never happen
            assert$DisplayableError(
              delta + compl === constantSum,
              "Constraint equation is not satisfied"
            );

            return delta;
          }
        }
      })();

      if (!delta) return { type: "skip-wallet-editions" };
      return numEditionsNew < numEditionsOld
        ? { type: "transfer-to-wallet", numEditions: delta }
        : numEditionsNew > numEditionsOld
        ? { type: "transfer-from-wallet", numEditions: delta }
        : { type: "skip-wallet-editions" };
    })();

    onSubmit?.(event);
  });

  return (
    <ModalV2 open={open} onClose={onCancel} closeOnDimmerClick closeOnEscape>
      <ModalV2.HeaderV2
        title="Update listing details (2/2)"
        description="Update the number of Editions and price of your listed Artwork."
      />
      <ModalV2.Content>
        {numEditionsOld === numEditionsNew ? (
          <SummaryDescription
            numEditions={numEditionsNew}
            listingPrice={listingPrice}
          />
        ) : numEditionsNew < numEditionsOld ? (
          <Flex.Col gap="32px">
            <div className={styles.textPrompt}>
              <span>{"You want to decrease the listing by "}</span>
              <strong>{numEditionsOld - numEditionsNew}</strong>
              <span>{" Editions. Do you want to withdraw some to your"}</span>
              <span>{" wallets? The rest will be burned. "}</span>
            </div>
            <RadioGroup.Root
              value={selectedOption}
              onChange={setSelectedOption}
              allowedValues={["none", "max", "custom"]}
            >
              <Flex.Col gap="20px">
                <RadioGroup.Item value="none">
                  <span>{`I want to burn `}</span>
                  <strong>{constantSum}</strong>
                  <span>{" existing editions"}</span>
                </RadioGroup.Item>
                <RadioGroup.Item value="max">
                  <span>{`I want to transfer `}</span>
                  <strong>{maxWalletDelta}</strong>
                  <span>{" editions to my wallet"}</span>
                </RadioGroup.Item>
                <RadioGroup.Item value="custom" content="Custom" />
                <FieldNumEditions
                  style={
                    selectedOption !== "custom"
                      ? { display: "none" }
                      : undefined
                  }
                  label="Transfer to your wallet"
                  tooltip="Withdraw some listed Editions from the Kreate marketplace back to your wallet for personal use."
                  api={fieldNumEditionsFromOrToWallet}
                  disabled={busy || selectedOption !== "custom"}
                  showMaxButton
                />
                <FieldNumEditions
                  style={
                    selectedOption !== "custom"
                      ? { display: "none" }
                      : undefined
                  }
                  label="Burn"
                  tooltip="Destroy some listed Editions."
                  api={fieldNumEditionsMintedOrBurned}
                  disabled={busy || selectedOption !== "custom"}
                  showMaxButton
                />
              </Flex.Col>
            </RadioGroup.Root>
            <SummaryDescription
              numEditions={numEditionsNew}
              listingPrice={listingPrice}
            />
          </Flex.Col>
        ) : numEditionsInWallet > 0 ? (
          <Flex.Col gap="32px">
            <div className={styles.textPrompt}>
              <span>{"You need to list "}</span>
              <strong>{numEditionsNew - numEditionsOld}</strong>
              <span>{" more Editions. Your wallet currently has "}</span>
              <strong>{numEditionsInWallet}</strong>
              <span>{". Do you want to transfer some Editions "}</span>
              <span>{"from your wallet? The rest will be minted anew."}</span>
            </div>
            <RadioGroup.Root
              value={selectedOption}
              onChange={setSelectedOption}
              allowedValues={["none", "max", "custom"]}
            >
              <Flex.Col gap="20px">
                <RadioGroup.Item value="none">
                  <span>{`I want to mint `}</span>
                  <strong>{constantSum}</strong>
                  <span>{" new editions"}</span>
                </RadioGroup.Item>
                <RadioGroup.Item value="max">
                  <span>{`I want to transfer `}</span>
                  <strong>{maxWalletDelta}</strong>
                  <span>{" editions from my wallet"}</span>
                  {maxWalletDelta < constantSum ? (
                    <>
                      <span>{" and mint "}</span>
                      <strong>{constantSum - maxWalletDelta}</strong>
                      <span>{" new editions"}</span>
                    </>
                  ) : undefined}
                </RadioGroup.Item>
                <RadioGroup.Item value="custom" content="Custom" />
                <FieldNumEditions
                  style={
                    selectedOption !== "custom"
                      ? { display: "none" }
                      : undefined
                  }
                  label="Transfer from your wallet"
                  api={fieldNumEditionsFromOrToWallet}
                  disabled={busy || selectedOption !== "custom"}
                  showMaxButton
                />
                <FieldNumEditions
                  style={
                    selectedOption !== "custom"
                      ? { display: "none" }
                      : undefined
                  }
                  label="Mint"
                  api={fieldNumEditionsMintedOrBurned}
                  disabled={busy || selectedOption !== "custom"}
                  showMaxButton
                />
              </Flex.Col>
            </RadioGroup.Root>
            <SummaryDescription
              numEditions={numEditionsNew}
              listingPrice={listingPrice}
            />
          </Flex.Col>
        ) : (
          <SummaryDescription
            numEditions={numEditionsNew}
            listingPrice={listingPrice}
          />
        )}
      </ModalV2.Content>
      <ModalV2.Actions>
        <ButtonOutline content="Cancel" onClick={onCancel} disabled={busy} />
        <ButtonPrimary content="Apply" onClick={handleSubmit} disabled={busy} />
      </ModalV2.Actions>
    </ModalV2>
  );
}
